import { TFunction } from 'i18next';
import { Dispatch, SetStateAction } from 'react';
import { apiClientWithoutHandler } from 'src/js/api';
import { APIPath, APIPathWithArguments } from 'src/js/api/route-path-index';
import { notificationHandler } from 'src/js/notification/notification-handler';
import { createWatchStore, updateWatchStore } from 'src/js/store/use-watch';

import {
  AudioEncoderResponse,
  AudioEncoderViewModel,
  GetAllAudioEncodersResponse,
  mapAudioEncoderViewModel,
  mapAudioEncodersResponse,
} from './audio-encoder-models';

const audioEncoders: AudioEncoderViewModel[] = [];
createWatchStore(audioEncoders);
export default audioEncoders;

export const saveResponseToAudioEncoders = (response: AudioEncoderViewModel[]) => {
  updateWatchStore(audioEncoders, response);
};

const replaceViewModel = (response: AudioEncoderResponse, t: TFunction, selected = false) => {
  const viewModel = mapAudioEncoderViewModel(response, t);
  const encoders = [...audioEncoders];
  const index = encoders.findIndex((e) => e.id === viewModel.id);
  if (index > -1) {
    encoders.splice(index, 1, { ...viewModel, selected: selected });
  }

  saveResponseToAudioEncoders(encoders);
};

export const fetchAudioEncoders = (t: TFunction) => {
  return apiClientWithoutHandler.genericController.get<GetAllAudioEncodersResponse>(APIPath.audenc.list).then((res) => {
    if (res.ok) {
      const response = res.data;
      if (response) {
        const viewModels = mapAudioEncodersResponse(response, t);
        saveResponseToAudioEncoders(viewModels);
      }
    }
    return res;
  });
};

export const startStopAudioEncoder = (
  model: AudioEncoderViewModel,
  start: boolean,
  selected: boolean,
  t: TFunction,
) => {
  const path = APIPathWithArguments(start ? APIPath.audenc.start : APIPath.audenc.stop, {
    id: model.id,
  });
  return apiClientWithoutHandler.genericController
    .put<AudioEncoderResponse>(path)
    .then((res) => {
      if (res.ok) {
        const response = res.data;
        if (response) {
          replaceViewModel(response, t, selected);
        }
      }
      return res;
    })
    .then((res) => {
      notificationHandler(res, null, null, t);
      return res;
    });
};

export const muteUnmuteAudioEncoder = (
  model: AudioEncoderViewModel,
  unmute: boolean,
  selected: boolean,
  t: TFunction,
) => {
  const path = APIPathWithArguments(unmute ? APIPath.audenc.unmute : APIPath.audenc.mute, {
    id: model.id,
  });
  return apiClientWithoutHandler.genericController
    .put<AudioEncoderResponse>(path)
    .then((res) => {
      if (res.ok) {
        const response = res.data;
        if (response) {
          replaceViewModel(response, t, selected);
        }
      }
      return res;
    })
    .then((res) => {
      notificationHandler(res, null, null, t);
      return res;
    });
};

export const fetchAudioEncoderStatistics = (
  model: AudioEncoderViewModel,
  t: TFunction,
  setStatistics: Dispatch<SetStateAction<AudioEncoderViewModel>>,
) => {
  const path = APIPathWithArguments(APIPath.audenc.stats, { id: model.id });
  return apiClientWithoutHandler.genericController.get<AudioEncoderResponse>(path).then((res) => {
    if (res.ok) {
      const response = res.data;
      if (response) {
        setStatistics(mapAudioEncoderViewModel(response, t));
      }
    }
    return res;
  });
};

export const resetAudioEncoderStatistics = (
  model: AudioEncoderViewModel,
  t: TFunction,
  setStatistics: Dispatch<SetStateAction<AudioEncoderViewModel>>,
) => {
  const path = APIPathWithArguments(APIPath.audenc.stats, { id: model.id });
  return apiClientWithoutHandler.genericController.delete<AudioEncoderResponse>(path).then((res) => {
    if (res.ok) {
      const response = res.data;
      if (response) {
        setStatistics(mapAudioEncoderViewModel(response, t));
      }
    }
    return res;
  });
};

export const updateAudioEncoder = (model: AudioEncoderViewModel, t: TFunction) => {
  const path = APIPathWithArguments(APIPath.audenc.update, { id: model.id });
  return apiClientWithoutHandler.genericController
    .put<AudioEncoderResponse>(path, model)
    .then((res) => {
      if (res.ok) {
        const response = res.data;
        if (response) {
          replaceViewModel(response, t);
        }
      }
      return res;
    })
    .then((res) => {
      notificationHandler(
        res,
        t('audioEncoder.notifications.settingsChangeSuccessMsg'),
        t('audioEncoder.notifications.settingsChangeErrorMsg'),
        t,
      );
      return res;
    });
};
